import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MailIcon, FileTextIcon, SendIcon, EyeIcon, ArrowLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';


export const Route = createFileRoute(
    '/_app/$module_id/$entity_type_id/segments/$segment_id/communications/$template_id',
)({
    component: CommunicationPreview,
    validateSearch: (search) => {
        return {
            entityId: search.entityId,
            filter: search.filter
        }
    }
})

// interface PreviewParams {
//     module_id: string;
//     entity_type_id: string;
//     segment_id: string;
//     template_id: string;
// }

// interface PreviewSearch {
//     entityId?: string;  // For single entity preview
//     filter?: string;    // For segment preview with filter
// }

function CommunicationPreview() {
    const { entity_type_id, segment_id, template_id } = Route.useParams();
    const { entityId, filter } = Route.useSearch();
    const navigate = useNavigate();
    const [recipients, setRecipients] = useState<string[]>([]);
    const [newRecipient, setNewRecipient] = useState('');
    const [parameters, setParameters] = useState<Record<string, any>>({});
    const [previewData, setPreviewData] = useState<any>(null);

    // Fetch template details
    const { data: template, isLoading: templateLoading } = useQuery({
        queryKey: ['communication-template', template_id],
        queryFn: async () => {
            const response = await axios.get(`/communication/templates/${entity_type_id}`, {
                params: { templateId: template_id }
            });
            return response.data.templates.find((t: any) => t.id === template_id);
        }
    });

    // Preview mutation
    const previewMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(`/communications/preview/${template_id}`, {
                entityId,
                segmentId: segment_id,
                entityType: entity_type_id,
                filter: filter || undefined,
                mode: template?.type || 'email',
                recipients,
                parameters
            });
            return response.data;
        },
        onSuccess: (result) => {
            setPreviewData(result);
        },
        onError: (error: any) => {
            toast.error(`Failed to preview: ${error.message}`);
        }
    });

    // Send mutation
    const sendMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(`/communications/send/${template_id}`, {
                entityId,
                segmentId: segment_id,
                entityType: entity_type_id,
                filter: filter || undefined,
                recipients,
                parameters
            });
            return response.data;
        },
        onSuccess: () => {
            toast.success(`${template?.type === 'email' ? 'Email' : 'Document'} sent successfully!`);
            navigate({ to: '..' });
        },
        onError: (error: any) => {
            toast.error(`Failed to send: ${error.message}`);
        }
    });

    const handlePreview = () => {
        previewMutation.mutate();
    };

    const handleSend = () => {
        if (template?.type === 'email' && recipients.length === 0) {
            toast.error('Please add at least one recipient');
            return;
        }
        sendMutation.mutate();
    };

    const addRecipient = () => {
        if (newRecipient.trim() && !recipients.includes(newRecipient.trim())) {
            setRecipients([...recipients, newRecipient.trim()]);
            setNewRecipient('');
        }
    };

    const removeRecipient = (email: string) => {
        setRecipients(recipients.filter(r => r !== email));
    };

    if (templateLoading) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Template not found</p>
                <Button onClick={() => navigate({ to: '..' })} className="mt-4">
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back
                </Button>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        {template.type === 'email' ? (
                            <MailIcon className="h-6 w-6 text-blue-600" />
                        ) : (
                            <FileTextIcon className="h-6 w-6 text-green-600" />
                        )}
                        {template.name}
                    </h1>
                    <p className="text-gray-600 mt-1">{template.description}</p>
                </div>
                <Button variant="outline" onClick={() => navigate({ to: '..' })}>
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Configuration</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {template.type === 'email' && (
                            <div className="space-y-2">
                                <Label>Recipients</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={newRecipient}
                                        onChange={(e) => setNewRecipient(e.target.value)}
                                        placeholder="Add recipient email"
                                        onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                                    />
                                    <Button onClick={addRecipient}>Add</Button>
                                </div>
                                {recipients.length > 0 && (
                                    <div className="space-y-2">
                                        {recipients.map((email) => (
                                            <Badge
                                                key={email}
                                                variant="secondary"
                                                className="flex items-center gap-2"
                                            >
                                                {email}
                                                <button
                                                    onClick={() => removeRecipient(email)}
                                                    className="hover:text-red-600"
                                                >
                                                    ×
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Add any template-specific parameters here */}
                        {template.parameters?.map((param: any) => (
                            <div key={param.name} className="space-y-2">
                                <Label>{param.label}</Label>
                                {param.type === 'textarea' ? (
                                    <Textarea
                                        value={parameters[param.name] || ''}
                                        onChange={(e) => setParameters({
                                            ...parameters,
                                            [param.name]: e.target.value
                                        })}
                                        placeholder={param.placeholder}
                                    />
                                ) : (
                                    <Input
                                        type={param.type || 'text'}
                                        value={parameters[param.name] || ''}
                                        onChange={(e) => setParameters({
                                            ...parameters,
                                            [param.name]: e.target.value
                                        })}
                                        placeholder={param.placeholder}
                                    />
                                )}
                            </div>
                        ))}

                        <div className="flex gap-3 pt-4">
                            <Button
                                onClick={handlePreview}
                                variant="outline"
                                disabled={previewMutation.isPending}
                                className="gap-2"
                            >
                                <EyeIcon className="h-4 w-4" />
                                Preview
                            </Button>
                            <Button
                                onClick={handleSend}
                                disabled={sendMutation.isPending || (template.type === 'email' && recipients.length === 0)}
                                className="gap-2"
                            >
                                <SendIcon className="h-4 w-4" />
                                {sendMutation.isPending ? 'Sending...' : 'Send'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Preview</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {previewMutation.isPending ? (
                            <div className="flex items-center justify-center p-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : previewData ? (
                            <div className="space-y-4">
                                {template.type === 'email' ? (
                                    <>
                                        <div className="border-b pb-2">
                                            <div className="text-sm text-gray-600">Subject</div>
                                            <div className="font-medium">{previewData.subject}</div>
                                        </div>
                                        <div className="prose max-w-none">
                                            <div dangerouslySetInnerHTML={{ __html: previewData.content }} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="aspect-[3/4] bg-gray-100 rounded-lg flex items-center justify-center">
                                        <FileTextIcon className="h-12 w-12 text-gray-400" />
                                        <span className="ml-2 text-gray-600">Document Preview</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                Click Preview to see how this {template.type} will look
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
} 
